import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';

// UI label ↔ enum value pairs (schema-compatible)
const CATEGORY_OPTIONS = [
  { label: 'Wedding', value: 'wedding' },
  { label: 'Birthday', value: 'birthday' },
  { label: 'Anniversary', value: 'anniversary' },
  { label: 'Corporate', value: 'corporate' },
  { label: 'Seasonal', value: 'seasonal' },
  { label: 'Other', value: 'other' },
];

const MAX_IMAGES = 3;

function toLabelFromEnum(v) {
  const found = CATEGORY_OPTIONS.find(o => o.value === v);
  return found?.label ?? 'Other';
}

export default function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [filter, setFilter] = useState('All'); // holds either 'All' or enum value
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/api/gallery');
      setItems(data?.items || []);
    } catch (e) {
      console.error('[GalleryAdmin] Failed to load gallery', e);
      alert(e?.response?.data?.message || 'Failed to load gallery.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    let list = [...items];
    if (filter !== 'All') list = list.filter(i => i.category === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.title?.toLowerCase().includes(q) ||
        i.description?.toLowerCase().includes(q) ||
        i.category?.toLowerCase().includes(q) ||
        (Array.isArray(i.tags) && i.tags.join(' ').toLowerCase().includes(q))
      );
    }
    return list;
  }, [items, filter, search]);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this gallery item? This cannot be undone.')) return;
    try {
      setSaving(true);
      await axiosInstance.delete(`/api/gallery/${id}`);
      setItems(prev => prev.filter(i => i._id !== id));
    } catch (e) {
      console.error('[GalleryAdmin] Delete failed', e);
      alert(e?.response?.data?.message || 'Failed to delete.');
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (item) => { setEditing(item); setFormOpen(true); };

  return (
    <div className="p-6 bg-candy-background">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-candy-primary">Gallery Manager</h2>
        <div className="flex gap-2">
          <button
            className="px-3 py-2 rounded bg-white border hover:bg-gray-50"
            onClick={fetchItems}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            className="px-3 py-2 rounded bg-candy-primary text-white hover:bg-candy-primaryDark"
            onClick={openCreate}
          >
            + New Item
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-semibold text-candy-primary mb-1">Category</label>
          <select
            className="p-2 border rounded bg-white"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          >
            <option value="All">All</option>
            {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-semibold text-candy-primary mb-1">Search</label>
          <input
            className="w-full p-2 border rounded"
            placeholder="Search title / description / category / tags"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-candy-primary">Loading gallery…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 bg-white border rounded-xl text-center text-gray-600">
          No items yet. Click “New Item” to add your first cake.
        </div>
      ) : (
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(item => {
            const coverIdx = typeof item.coverIndex === 'number' ? item.coverIndex : 0;
            const cover = item.images?.[coverIdx] || item.images?.[0];
            return (
              <div key={item._id} className="bg-white border rounded-xl overflow-hidden shadow-sm">
                <div className="relative aspect-[4/3] bg-gray-50">
                  {cover?.url ? (
                    <img
                      src={cover.url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No image
                    </div>
                  )}
                  {!!item.images?.length && (
                    <span className="absolute top-3 left-3 text-xs bg-black/70 text-white px-2 py-0.5 rounded">
                      {item.images.length} {item.images.length > 1 ? 'images' : 'image'}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <div className="font-semibold">{item.title || 'Untitled'}</div>
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 border">
                      {item.priceText || '—'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                    <span>{toLabelFromEnum(item.category)}</span>
                    <span className={`px-1.5 py-0.5 rounded border ${item.active ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                      {item.active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-gray-400">Order #{item.order ?? 0}</span>
                  </div>

                  <div className="text-sm text-gray-600 line-clamp-2 min-h-[2.5rem]">
                    {item.description || <span className="italic text-gray-400">No description</span>}
                  </div>

                  {Array.isArray(item.tags) && item.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {item.tags.map(t => (
                        <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-gray-100 border">{t}</span>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50"
                      onClick={() => openEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                      onClick={() => onDelete(item._id)}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {formOpen && (
        <GalleryFormModal
          isEdit={!!editing}
          initialItem={editing}
          onClose={() => setFormOpen(false)}
          onSaved={(savedItem, mode) => {
            setFormOpen(false);
            if (mode === 'create') {
              setItems(prev => [savedItem, ...prev]);
            } else {
              setItems(prev => prev.map(i => (i._id === savedItem._id ? savedItem : i)));
            }
          }}
        />
      )}
    </div>
  );
}

/* ===============================
   Modal: Create / Edit Gallery Item
   =============================== */
function GalleryFormModal({ isEdit, initialItem, onClose, onSaved }) {
  const [title, setTitle] = useState(initialItem?.title || '');
  // store enum value in state
  const [category, setCategory] = useState(initialItem?.category || CATEGORY_OPTIONS[0].value);
  const [description, setDescription] = useState(initialItem?.description || '');
  const [price, setPrice] = useState(initialItem?.priceText || '');
  const [active, setActive] = useState(
    typeof initialItem?.active === 'boolean' ? initialItem.active : true
  );
  const [order, setOrder] = useState(
    typeof initialItem?.order === 'number' ? initialItem.order : 0
  );
  const [tagsInput, setTagsInput] = useState(
    Array.isArray(initialItem?.tags) ? initialItem.tags.join(', ') : ''
  );

  const [coverIndex, setCoverIndex] = useState(
    typeof initialItem?.coverIndex === 'number' ? initialItem.coverIndex : 0
  );

  const [existingImages, setExistingImages] = useState(initialItem?.images || []); // from server
  const [removeKeys, setRemoveKeys] = useState([]); // storagePath[]
  const [newFiles, setNewFiles] = useState([]); // File[]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(initialItem?.title || '');
    setCategory(initialItem?.category || CATEGORY_OPTIONS[0].value);
    setDescription(initialItem?.description || '');
    setPrice(initialItem?.priceText || '');
    setActive(typeof initialItem?.active === 'boolean' ? initialItem.active : true);
    setOrder(typeof initialItem?.order === 'number' ? initialItem.order : 0);
    setTagsInput(Array.isArray(initialItem?.tags) ? initialItem.tags.join(', ') : '');
    setCoverIndex(typeof initialItem?.coverIndex === 'number' ? initialItem.coverIndex : 0);
    setExistingImages(initialItem?.images || []);
    setRemoveKeys([]);
    setNewFiles([]);
  }, [initialItem]);

  const activeExisting = existingImages.filter(i => !removeKeys.includes(i.storagePath));
  const totalImages = activeExisting.length + newFiles.length;

  const pickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = /^image\/(png|jpe?g|webp|gif)$/;
    const valid = files.filter(f => allowed.test(f.type));
    if (valid.length !== files.length) {
      alert('Only images (PNG/JPG/WEBP/GIF) are allowed.');
      return;
    }
    const remaining = MAX_IMAGES - totalImages;
    if (remaining <= 0) {
      alert(`You can upload up to ${MAX_IMAGES} images per item.`);
      return;
    }
    const chosen = valid.slice(0, remaining);
    setNewFiles(prev => [...prev, ...chosen]);
    e.target.value = '';
  };

  const removeLocal = (idx) => setNewFiles(prev => prev.filter((_, i) => i !== idx));
  const toggleRemoveExisting = (img) => {
    setRemoveKeys(prev =>
      prev.includes(img.storagePath) ? prev.filter(k => k !== img.storagePath) : [...prev, img.storagePath]
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return alert('Please enter a title.');
    if (totalImages < 1) return alert('Please attach at least 1 image (up to 3).');
    if (totalImages > MAX_IMAGES) return alert(`Up to ${MAX_IMAGES} images allowed.`);

    // prepare tags array
    const tags = tagsInput
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    setSaving(true);
    try {
      // Build FormData for single-request contract
      const fd = new FormData();
      fd.append('title', title.trim());

      // IMPORTANT: send schema enum value
      fd.append('category', category);

      fd.append('description', description.trim());
      fd.append('priceText', price || '');

      // Optional fields present in schema
      fd.append('active', String(!!active));
      fd.append('order', String(Number.isFinite(+order) ? +order : 0));

      // tags[] as multiple entries so backend gets an array
      tags.forEach(t => fd.append('tags', t));

      // images handling (backend should build ImageSchema objects)
      fd.append('coverIndex', String(Math.max(0, Math.min(coverIndex, MAX_IMAGES - 1))));
      removeKeys.forEach(k => fd.append('removeKeys', k));
      newFiles.forEach(f => fd.append('images', f, f.name));

      if (isEdit) {
        const { data } = await axiosInstance.put(`/api/gallery/${initialItem._id}`, fd);
        onSaved?.(data.item || data, 'update');
      } else {
        const { data } = await axiosInstance.post('/api/gallery', fd);
        onSaved?.(data.item || data, 'create');
      }
    } catch (e2) {
      console.error('[GalleryForm] Save failed', e2);
      alert(e2?.response?.data?.message || 'Failed to save item.');
    } finally {
      setSaving(false);
    }
  };

  const effectiveCoverIdx = Math.max(0, Math.min(coverIndex, totalImages - 1));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-white w-full max-w-2xl rounded-xl shadow-xl p-6 overflow-y-auto overscroll-y-contain max-h-[90dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">{isEdit ? 'Edit Gallery Item' : 'New Gallery Item'}</h3>
          <button className="px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200" onClick={onClose}>Close</button>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1">Title</label>
              <input
                className="w-full p-2 border rounded"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., Elegant Rose Wedding Cake"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Category</label>
              <select
                className="w-full p-2 border rounded bg-white"
                value={category}
                onChange={e => setCategory(e.target.value)}
              >
                {CATEGORY_OPTIONS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Description</label>
            <textarea
              rows={3}
              className="w-full p-2 border rounded"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Short description…"
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className="block text-xs font-semibold mb-1">Price (priceText)</label>
              <input
                className="w-full p-2 border rounded"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="$250"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-xs font-semibold mb-1">Order</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                value={order}
                onChange={e => setOrder(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="sm:col-span-1 flex items-end">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={e => setActive(e.target.checked)}
                />
                <span className="text-sm">Active</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Tags (comma-separated)</label>
            <input
              className="w-full p-2 border rounded"
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              placeholder="e.g., fondant, floral, 3-tier"
            />
          </div>

          {/* Images */}
          <div>
            <label className="block text-xs font-semibold mb-2">Images (up to 3)</label>
            <div className="grid grid-cols-3 gap-3">
              {/* existing images kept */}
              {activeExisting.map((img, idx) => (
                <div key={img.storagePath || img.url || idx} className="relative border rounded overflow-hidden">
                  <img
                    src={img.url}
                    alt={img.originalName || 'image'}
                    className="w-full aspect-square object-cover"
                    crossOrigin="anonymous"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-xs p-1 flex items-center justify-between">
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="cover"
                        checked={effectiveCoverIdx === idx}
                        onChange={() => setCoverIndex(idx)}
                      />
                      Cover
                    </label>
                    <button
                      type="button"
                      className="px-2 py-0.5 bg-red-600 rounded"
                      onClick={() => toggleRemoveExisting(img)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}

              {/* new local files */}
              {newFiles.map((f, i) => {
                const idx = activeExisting.length + i;
                const preview = URL.createObjectURL(f);
                return (
                  <div key={`${f.name}-${i}`} className="relative border rounded overflow-hidden">
                    <img src={preview} alt={f.name} className="w-full aspect-square object-cover" />
                    <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-xs p-1 flex items-center justify-between">
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          name="cover"
                          checked={effectiveCoverIdx === idx}
                          onChange={() => setCoverIndex(idx)}
                        />
                        Cover
                      </label>
                      <button
                        type="button"
                        className="px-2 py-0.5 bg-red-600 rounded"
                        onClick={() => removeLocal(i)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* add tile */}
              {totalImages < MAX_IMAGES && (
                <label className="border rounded flex flex-col items-center justify-center aspect-square cursor-pointer hover:bg-gray-50">
                  <span className="text-3xl text-gray-400">＋</span>
                  <span className="text-xs text-gray-500 mt-1">Add image</span>
                  <input type="file" accept="image/*" className="hidden" onChange={pickFiles} />
                </label>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">{totalImages}/{MAX_IMAGES} images selected.</p>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" className="px-4 py-2 border rounded bg-white hover:bg-gray-50" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-candy-primary text-white hover:bg-candy-primaryDark disabled:opacity-60"
              disabled={saving}
            >
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Item')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
